// web/src/contact/ContactPage.tsx
//
// /contact-us route. Detects session presence via getAccessToken() and
// switches ContactForm into authenticated mode (with prefilled
// email/name + no Turnstile) when signed in, otherwise renders the
// anonymous form.
//
// Public route: rendered outside <RequireAuth>. Header + footer come
// from MarketingLayout.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { meApi } from "@/api/me";
import { getAccessToken } from "@/api/session";
import { Seo } from "@/marketing/Seo";
import { ContactForm } from "./ContactForm";

export function ContactPage() {
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAccessToken().then((token) => {
      if (!cancelled) setHasSession(!!token);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["me"],
    queryFn: meApi.get,
    enabled: hasSession === true,
  });

  // Don't mount ContactForm until we know whether we're in authed mode and
  // (if so) we have the user's email. ContactForm captures prefilledEmail in
  // initial state, so mounting before the email is known would lock it to "".
  const ready =
    hasSession === false || (hasSession === true && !meLoading && me !== undefined);
  const prefilledEmail =
    hasSession === true && me ? me.email ?? undefined : undefined;

  return (
    <main className="container mx-auto max-w-2xl px-4 py-12">
      <Seo
        title="Contact us — Tapelens"
        description="Send us a message and we'll respond within 5 business days. Privacy or data requests go to legal@tapelens.com."
        path="/contact-us"
      />
      <header className="mb-8 space-y-2 border-b border-border pb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Contact us</h1>
        <p className="text-sm text-muted-foreground">
          Send us a message and we'll respond within 5 business days. For privacy
          requests, choose "Privacy or data request" — those go to legal@tapelens.com.
        </p>
      </header>
      {ready && (
        <ContactForm prefilledEmail={prefilledEmail} prefilledName={undefined} />
      )}
    </main>
  );
}
